import React, { ReactNode, useContext } from 'react'

import { RemarkReport, TestProps } from '@/@types/global'
import { Bending } from '@/@types/report/bending'
import { Chemical } from '@/@types/report/chemical'
import { CopperLayer } from '@/@types/report/copperLayer'
import { Ctod } from '@/@types/report/ctod'
import { Flaring } from '@/@types/report/flaring'
import { Flattening } from '@/@types/report/flattening'
import { FourPoint } from '@/@types/report/fourPoint'
import { Hardness } from '@/@types/report/hardness'
import { Hic } from '@/@types/report/hic'
import { Impact } from '@/@types/report/impact'
import { MicroCleanlines } from '@/@types/report/microCleanlines'
import { MicroHardness } from '@/@types/report/microHardness'
import { MicroStructure } from '@/@types/report/microStructure'
import { MicroStructurePhotos } from '@/@types/report/microStructurePhotos'
import { NaceA } from '@/@types/report/naceA'
import { NaceD } from '@/@types/report/naceD'
import { ReportItems } from '@/@types/report/report-items'
import { SITES, TESTS } from '@/utils/constants'

interface SearchReportContextInterface {
  site: string
  setSite: (site: string) => void
  mappedSites: { key: number; value: string; label: string }[]
  test: string
  setTest: (test: string) => void
  mappedTests: { key: number; value: string; label: string }[]
  tests: string[]
  setTests: (tests: string[]) => void
  startDate: Date | undefined
  setStartDate: (startDate: Date | undefined) => void
  endDate: Date | undefined
  setEndDate: (endDate: Date | undefined) => void
  project: string
  setProject: (project: string) => void
  projects: string[]
  setProjects: (projects: string[]) => void
  orderItem: string
  setOrderItem: (orderItem: string) => void
  orderItems: string[]
  setOrderItems: (orderItems: string[]) => void
  typeInspection: string
  setTypeInspection: (typeInspection: string) => void
  typeInspections: string[]
  setTypeInspections: (typeInspections: string[]) => void
  type: string
  setType: (type: string) => void
  types: string[]
  setTypes: (types: string[]) => void
  clientHeader: string
  setClientHeader: (clientHeader: string) => void
  descriptionHeader: string
  setDescriptionHeader: (descriptionHeader: string) => void
  projectHeader: string
  setProjectHeader: (projectHeader: string) => void
  inspectionHeader: string
  setInspectionHeader: (inspectionHeader: string) => void
  pedidoItemHeader: string
  setPedidoItemHeader: (pedidoItemHeader: string) => void
  currentItem:
    | FourPoint
    | Flattening
    | Flaring
    | CopperLayer
    | Bending
    | Chemical
    | Ctod
    | Hardness
    | Hic
    | Impact
    | MicroCleanlines
    | MicroHardness
    | MicroStructure
    | MicroStructurePhotos
    | NaceA
    | NaceD
    | undefined
  setCurrentItem: (
    value:
      | FourPoint
      | Flattening
      | Flaring
      | CopperLayer
      | Bending
      | Chemical
      | Ctod
      | Hardness
      | Hic
      | Impact
      | MicroCleanlines
      | MicroHardness
      | MicroStructure
      | MicroStructurePhotos
      | NaceA
      | NaceD
  ) => void
  testSelected: TestProps | undefined
  setTestSelected: (value: TestProps | undefined) => void
  heatItems: string[] | undefined
  setHeatItems: (value: string[] | undefined) => void
  processOrderItems: string[] | undefined
  setProcessOrderItems: (value: string[] | undefined) => void
  resultItemsSelected: ReportItems[] | undefined
  setResultItemsSelected: (value: ReportItems[] | undefined) => void
  remark1: string | undefined
  setRemark1: (value: string) => void
  remark2: string | undefined
  setRemark2: (value: string) => void
  remark3: string | undefined
  setRemark3: (value: string) => void
  templateTest: any[] | undefined
  setTemplateTest: (value: any[] | undefined) => void
  identificationApprovalFlow: string
  setIdentificationApprovalFlow: (value: string) => void
  completeHeaderItem: any
  setCompleteHeaderItem: (value: any | undefined) => void
}

export const SearchReportContext =
  React.createContext<SearchReportContextInterface>(
    {} as SearchReportContextInterface
  )

interface SearchReportProviderProps {
  children: ReactNode
}

const SearchReportProvider = ({ children }: SearchReportProviderProps) => {
  const [site, setSite] = React.useState('')
  const mappedSites = SITES.map((s) => ({
    key: s.id,
    value: s.id?.toString() || '',
    label: s.name,
  }))
  const mappedTests = TESTS.map((s) => ({
    key: s.id,
    value: s.id?.toString() || '',
    label: s.name,
  }))
  const [test, setTest] = React.useState<string>('')
  const [tests, setTests] = React.useState<string[]>([])
  const [startDate, setStartDate] = React.useState<Date | undefined>(undefined)
  const [endDate, setEndDate] = React.useState<Date | undefined>(undefined)
  const [project, setProject] = React.useState('')
  const [projects, setProjects] = React.useState<string[]>([])
  const [orderItem, setOrderItem] = React.useState('')
  const [orderItems, setOrderItems] = React.useState<string[]>([])
  const [typeInspection, setTypeInspection] = React.useState('')
  const [typeInspections, setTypeInspections] = React.useState<string[]>([])
  const [type, setType] = React.useState('')
  const [types, setTypes] = React.useState<string[]>([])
  const [clientHeader, setClientHeader] = React.useState('')
  const [descriptionHeader, setDescriptionHeader] = React.useState('')
  const [projectHeader, setProjectHeader] = React.useState('')
  const [inspectionHeader, setInspectionHeader] = React.useState('')
  const [pedidoItemHeader, setPedidoItemHeader] = React.useState('')
  const [heatItems, setHeatItems] = React.useState<string[] | undefined>(
    undefined
  )
  const [testSelected, setTestSelected] = React.useState<TestProps | undefined>(
    undefined
  )
  const [processOrderItems, setProcessOrderItems] = React.useState<
    string[] | undefined
  >(undefined)
  const [resultItemsSelected, setResultItemsSelected] = React.useState<
    ReportItems[] | undefined
  >(undefined)
  const [currentItem, setCurrentItem] = React.useState<
    | FourPoint
    | Flattening
    | Flaring
    | CopperLayer
    | Bending
    | Chemical
    | Ctod
    | Hardness
    | Hic
    | Impact
    | MicroCleanlines
    | MicroHardness
    | MicroStructure
    | MicroStructurePhotos
    | NaceA
    | NaceD
  >()
  const [remark1, setRemark1] = React.useState('')
  const [remark2, setRemark2] = React.useState('')
  const [remark3, setRemark3] = React.useState('')
  const [templateTest, setTemplateTest] = React.useState<any[] | undefined>(
    undefined
  )
  const [identificationApprovalFlow, setIdentificationApprovalFlow] =
    React.useState('')
  const [completeHeaderItem, setCompleteHeaderItem] = React.useState(undefined)

  return (
    <SearchReportContext.Provider
      value={{
        site,
        setSite,
        mappedSites,
        test,
        setTest,
        mappedTests,
        tests,
        setTests,
        startDate,
        setStartDate,
        endDate,
        setEndDate,
        project,
        setProject,
        projects,
        setProjects,
        orderItem,
        setOrderItem,
        orderItems,
        setOrderItems,
        typeInspection,
        setTypeInspection,
        typeInspections,
        setTypeInspections,
        type,
        setType,
        types,
        setTypes,
        clientHeader,
        setClientHeader,
        descriptionHeader,
        setDescriptionHeader,
        projectHeader,
        setProjectHeader,
        inspectionHeader,
        setInspectionHeader,
        pedidoItemHeader,
        setPedidoItemHeader,
        currentItem,
        setCurrentItem,
        testSelected,
        setTestSelected,
        heatItems,
        setHeatItems,
        processOrderItems,
        setProcessOrderItems,
        resultItemsSelected,
        setResultItemsSelected,
        remark1,
        setRemark1,
        remark2,
        setRemark2,
        remark3,
        setRemark3,
        templateTest,
        setTemplateTest,
        identificationApprovalFlow,
        setIdentificationApprovalFlow,
        completeHeaderItem,
        setCompleteHeaderItem,
      }}
    >
      {children}
    </SearchReportContext.Provider>
  )
}

function useSearchReport(): SearchReportContextInterface {
  return useContext(SearchReportContext)
}

export { SearchReportProvider, useSearchReport }
